import { useState, useEffect } from 'react'
import { InfoTip } from '../../components/InfoTip'
import { TraderSetSidebar } from '../../components/TraderSetSidebar'
import { useExecutions, categorizeExecutions } from '../../context/ExecutionContext'
import { getLotSize } from '../../utils/lotSize'
import { useToast } from '../../context/ToastContext'
import { useAuth } from '../../context/AuthContext'
import { useAllBrokerFunds } from '../../hooks/useBrokerFunds'
import type { StrategyExecution } from '../../types'


export function TraderDashboard({ onNavigate: _onNavigate }: { onNavigate: (v: any) => void }) {
  const {
    executions: execs, startExecution, stopExecution, partialExit, panicExit, createExecution, refreshExecutions,
  } = useExecutions()
  const { addToast } = useToast()
  const { session } = useAuth()

  const [showSidebar, setShowSidebar] = useState(false)
  const [partialModal, setPartialModal] = useState<string | null>(null)
  const [partialCe, setPartialCe] = useState(0)
  const [partialPe, setPartialPe] = useState(0)
  const [panicModal, setPanicModal] = useState<string | null>(null)
  const [legsExpanded, setLegsExpanded] = useState<Record<string, boolean>>({})

  // Live broker funds / margin
  const { data: allFunds, loading: fundsLoading, refresh: refreshFunds } = useAllBrokerFunds()
  // Aggregate net available across all connected brokers
  const totalNetAvailable = Object.values(allFunds).reduce((sum, f) => sum + (f.netAvailable ?? 0), 0)
  const hasFundsData = Object.keys(allFunds).length > 0

  // Fetch real data on mount
  useEffect(() => {
    refreshExecutions()
  }, [refreshExecutions])

  const handleCreateFromSidebar = (data: { stratType: 'rsi_straddle' | 'trending_expiry'; underlying: string; lots: number; broker: string; mode: 'single_client' | 'multi_client'; executionMode: 'scheduled' | 'instant'; customName: string; params: Record<string, any> }) => {
    createExecution({
      strategyId: data.stratType === 'rsi_straddle' ? 's1' : 's3',
      strategyType: data.stratType,
      setName: data.customName || `${data.stratType === 'rsi_straddle' ? 'RSI' : 'Trend'} — ${data.underlying}`,
      traderId: session?.user?.id || 'trader1',
      underlying: data.underlying,
      mode: data.mode,
      brokerId: data.broker,
      status: 'stopped',
      params: data.params,
      state: {
        currentPremium: 0, initialPremium: 0, slPrice: 0, targetPrice: 0, reentryCount: 0, pnl: 0,
        mtm: { strategyPnl: 0, setPnl: 0, totalPnl: 0, unrealizedPnl: 0, realizedPnl: 0, lastUpdated: new Date().toISOString() },
        currentStraddle: null,
      },
    })
    addToast(`${data.customName || data.stratType} created on ${data.underlying}`, 'success')
    setShowSidebar(false)
  }

  // Categorize executions
  const { today, active, archived } = categorizeExecutions(execs)
  const allRunning = [...today, ...active].filter((e) => e.status === 'running')
  const totalPnl = execs.reduce((s, e) => s + (e.state.pnl || 0), 0)
  const totalMtm = execs.reduce((s, e) => s + (e.state.mtm?.totalPnl || 0), 0)

  const handlePartialExit = () => {
    if (!partialModal) return
    partialExit(partialModal, partialCe, partialPe)
    const exec = execs.find((e) => e.id === partialModal)
    addToast(`Partial exit executed on ${exec?.setName || partialModal} — CE: ${partialCe}%, PE: ${partialPe}%`, 'success')
    setPartialModal(null)
    setPartialCe(0)
    setPartialPe(0)
  }

  const handlePanicExit = () => {
    if (!panicModal) return
    if (panicModal === 'all') {
      panicExit('all')
      addToast('Panic exit — ALL positions squared off', 'warning', 6000)
    } else {
      const exec = execs.find((e) => e.setName === panicModal)
      if (exec) {
        panicExit('set', exec.id)
        addToast(`Panic exit — ${panicModal} squared off`, 'warning', 6000)
      }
    }
    setPanicModal(null)
  }

  const handleStart = (id: string) => {
    startExecution(id)
    const exec = execs.find((e) => e.id === id)
    addToast(`${exec?.setName || 'Execution'} started`, 'success')
  }

  const handleStop = (id: string) => {
    stopExecution(id)
    const exec = execs.find((e) => e.id === id)
    addToast(`${exec?.setName || 'Execution'} stopped`, 'warning')
  }

  // ─── Render a strategy execution card (compact 2-row) ─────────────────
  const renderExecCard = (exec: StrategyExecution, showActions = true) => {
    const isExpanded = legsExpanded[exec.id]
    // Use live broker funds if available, else fall back to marginPerLot from params
    const brokerFunds = allFunds[exec.brokerId]
    const liveNetAvailable = brokerFunds?.netAvailable ?? null
    const marginPerLot = (exec.params as any)?.marginPerLot || 150000
    const margin = liveNetAvailable !== null ? liveNetAvailable : marginPerLot
    const limit = marginPerLot * 0.02
    const loss = exec.state.pnl < 0 ? Math.abs(exec.state.pnl) : 0
    const ddPercent = Math.min(100, Math.round((loss / limit) * 100))
    let ddColor = '#22c55e'
    if (ddPercent >= 80) ddColor = '#ef4444'
    else if (ddPercent >= 40) ddColor = '#eab308'
    const openLegs = (exec.state.straddleEntries?.reduce((a, e) => a + (e.call.status === 'open' ? 1 : 0) + (e.put.status === 'open' ? 1 : 0), 0) || 0) + (exec.state.extraLegs?.filter(l => l.status === 'open').length || 0)
    const isRunning = exec.status === 'running'
    const isPaused = exec.status === 'paused'
    const isStopped = exec.status === 'stopped'
    const isSquaredOff = exec.status === 'squared_off'
    const statusColor = isRunning ? 'var(--green)' : isPaused ? 'var(--yellow)' : isSquaredOff ? 'var(--orange, #f97316)' : 'var(--red)'
    const statusLabel = isRunning ? 'RUNNING' : isPaused ? 'PAUSED' : isSquaredOff ? 'SQUARED OFF' : 'STOPPED'
    const p = exec.params as any
    const lots = p?.quantityLots || 1
    const lotSize = getLotSize(exec.underlying)
    const qty = lots * lotSize

    return (
      <div className="xc" key={exec.id} style={{ '--xc-accent': statusColor } as React.CSSProperties}>
        {/* ── Row 1: Identity + Chips + Status + Actions ── */}
        <div className="xc-row1">
          <div className="xc-id">
            <span className="xc-dot" style={{ background: statusColor }} />
            <span className="xc-name">{exec.setName}</span>
            <span className="xc-type">{exec.strategyType === 'rsi_straddle' ? 'RSI Straddle' : 'Trending'}</span>
          </div>
          <div className="xc-chips">
            <span className="xc-chip xc-chip--hl">{exec.underlying}</span>
            <span className="xc-chip">{p?.expiryType || 'weekly'}</span>
            <span className="xc-chip">{p?.strikeSelectionMode || 'ATM'}{p?.strikeOffset ? (p.strikeOffset > 0 ? `+${p.strikeOffset}` : p.strikeOffset) : ''}</span>
            <span className="xc-chip">{p?.positionType === 'strangle' ? 'Strangle' : 'Straddle'}</span>
            <span className="xc-chip">{exec.mode === 'single_client' ? 'Single' : 'Multi'}</span>
            <span className="xc-sep" />
            <span className="xc-chip xc-chip--mono">Lot: {lots}</span>
            <span className="xc-chip xc-chip--mono">Qty: {qty}</span>
          </div>
          <div className="xc-right">
            {exec.state.lastPartialExit && (
              <span className="xc-partial-flag" title={`Partial exit — CE: ${exec.state.lastPartialExit.cePercent}%, PE: ${exec.state.lastPartialExit.pePercent}%`}>
                ◐ Partial ({exec.state.lastPartialExit.cePercent}/{exec.state.lastPartialExit.pePercent}%)
              </span>
            )}
            <span className={`xc-status xc-status--${exec.status}`}>{statusLabel}</span>
            {showActions && (
              <div className="xc-btns">
                {(isStopped || isSquaredOff) && <button className="btn btn-sm btn-green" onClick={() => handleStart(exec.id)}>▶ Start</button>}
                {isRunning && <button className="btn btn-sm btn-red" onClick={() => handleStop(exec.id)}>■ Stop</button>}
                {isPaused && <button className="btn btn-sm btn-green" onClick={() => handleStart(exec.id)}>▶ Resume</button>}
                {!isSquaredOff && <button className="btn btn-sm btn-ghost" onClick={() => { setPartialCe(50); setPartialPe(50); setPartialModal(exec.id) }}>↔ Partial</button>}
                {!isSquaredOff && <button className="btn btn-sm btn-red" onClick={() => setPanicModal(exec.setName)}>⚠ Exit</button>}
              </div>
            )}
          </div>
        </div>

        {/* ── Row 2: Metrics + Greeks + VIX + Legs ── */}
        <div className="xc-row2">
          <div className="xc-metrics">
            <span className="xc-m"><span className="xc-ml">Premium</span><span className="xc-mv xc-mv--accent">₹{(exec.state.currentPremium || 0).toLocaleString()}</span></span>
            <span className="xc-m"><span className="xc-ml">P&L</span><span className={`xc-mv ${(exec.state.pnl || 0) >= 0 ? 'xc-mv--green' : 'xc-mv--red'}`}>{(exec.state.pnl || 0) >= 0 ? '+' : ''}₹{(exec.state.pnl || 0).toLocaleString()}</span></span>
            <span className="xc-m"><span className="xc-ml">MTM</span><span className={`xc-mv ${(exec.state.mtm?.totalPnl || 0) >= 0 ? 'xc-mv--green' : 'xc-mv--red'}`}>{(exec.state.mtm?.totalPnl || 0) >= 0 ? '+' : ''}₹{(exec.state.mtm?.totalPnl || 0).toLocaleString()}</span></span>
            <span className="xc-m xc-m--margin">
              <span className="xc-ml">Margin</span>
              <span className="xc-margin-wrap">
                <span className="xc-margin-track"><span className="xc-margin-fill" style={{ width: `${ddPercent}%`, background: ddColor }} /></span>
                <span className="xc-mv" style={{ color: ddColor }}>{ddPercent}%</span>
              </span>
            </span>
          </div>
          <span className="xc-sep" />
          <div className="xc-greeks">
            <span className="xc-g"><span className="xc-gl">Delta</span><span className="xc-gv">—</span></span>
            <span className="xc-g"><span className="xc-gl">Gamma</span><span className="xc-gv">—</span></span>
            <span className="xc-g"><span className="xc-gl">Theta</span><span className="xc-gv">—</span></span>
            <span className="xc-g"><span className="xc-gl">Vega</span><span className="xc-gv">—</span></span>
          </div>
          <span className="xc-sep" />
          {exec.state.vixRsi !== undefined ? (
            <span className="xc-vix" style={{ color: exec.state.isCooldown ? 'var(--red)' : 'var(--green)' }}>VIX {exec.state.vixRsi.toFixed(0)} {exec.state.isCooldown ? '⏳' : '✓'}</span>
          ) : <span className="xc-vix" style={{ color: 'var(--text-muted)' }}>VIX —</span>}
          <span className="xc-re">Re-entry: {exec.state.reentryCount || 0}</span>
          <button className="xc-legs-btn" onClick={() => setLegsExpanded(prev => ({ ...prev, [exec.id]: !prev[exec.id] }))}>
            {isExpanded ? '▲' : '▼'} {openLegs} Legs
          </button>
        </div>

        {/* ── Expandable Legs ── */}
        {isExpanded && exec.state.straddleEntries && exec.state.straddleEntries.length > 0 && (
          <div className="xc-legs">
            {exec.state.straddleEntries.map((entry) => (
              <div key={entry.id} className="xc-leg-row">
                <span className="xc-leg"><span className="xc-leg-dot" style={{ background: entry.call.status === 'open' ? 'var(--green)' : 'var(--text-muted)' }} /><strong>{entry.strike}CE</strong><span className="xc-leg-d">@₹{entry.call.entryPrice} SL ₹{entry.call.slPrice} Tgt ₹{entry.call.targetPrice}</span></span>
                <span className="xc-leg"><span className="xc-leg-dot" style={{ background: entry.put.status === 'open' ? 'var(--green)' : 'var(--text-muted)' }} /><strong>{entry.strike}PE</strong><span className="xc-leg-d">@₹{entry.put.entryPrice} SL ₹{entry.put.slPrice} Tgt ₹{entry.put.targetPrice}</span></span>
              </div>
            ))}
            {exec.state.extraLegs && exec.state.extraLegs.map((leg) => (
              <span key={leg.id} className="xc-leg"><span className="xc-leg-dot" style={{ background: leg.status === 'open' ? 'var(--accent)' : 'var(--text-muted)' }} /><strong>{leg.strike}{leg.optionType}</strong><span className="xc-leg-d">@₹{leg.entryPrice} SL ₹{leg.slPrice}</span><em style={{ fontSize: '0.5625rem', color: 'var(--text-muted)' }}>(adj)</em></span>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div>
      <div className="page-head">
        <h1>Trader Dashboard</h1>
        <p className="page-sub">Manage your execution sets, positions & MTM</p>
      </div>

      {/* ─── Unified KPI Strip ─── */}
      <div className="kpi-strip">
        <div className="kpi-card">
          <div className="kpi-label">Total MTM <InfoTip term="totalMtm" /></div>
          <div className={`kpi-value ${totalMtm >= 0 ? 'kpi-positive' : 'kpi-negative'}`}>
            {totalMtm >= 0 ? '+' : ''}₹{totalMtm.toLocaleString()}
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Running <InfoTip term="running" /></div>
          <div className="kpi-value kpi-positive">{allRunning.length}</div>
          <div className="kpi-sub">active executions</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Total P&L <InfoTip term="totalPnl" /></div>
          <div className={`kpi-value ${totalPnl >= 0 ? 'kpi-positive' : 'kpi-negative'}`}>
            {totalPnl >= 0 ? '+' : ''}₹{totalPnl.toLocaleString()}
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Today's Sets <InfoTip term="totalSets" /></div>
          <div className="kpi-value">{today.length}</div>
          <div className="kpi-sub">created today</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Brokers <InfoTip term="brokers" /></div>
          <div className="kpi-value">{Array.from(new Set(execs.map(e => e.brokerId).filter(Boolean))).length || 0}</div>
          <div className="kpi-sub">connected</div>
        </div>
        <div className="kpi-card" style={{ cursor: 'pointer' }} onClick={refreshFunds} title="Click to refresh margin">
          <div className="kpi-label">Net Available <InfoTip term="netAvailable" /></div>
          <div className="kpi-value" style={{ color: hasFundsData ? 'var(--green)' : 'var(--text-muted)', fontSize: hasFundsData ? undefined : '0.9rem' }}>
            {fundsLoading && !hasFundsData
              ? '…'
              : hasFundsData
                ? `₹${Math.round(totalNetAvailable).toLocaleString('en-IN')}`
                : 'N/A'}
          </div>
          <div className="kpi-sub">{hasFundsData ? 'live funds' : 'broker offline'}</div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <button className="btn btn-primary btn-sm" onClick={() => setShowSidebar(true)}>+ New Set</button>
          <button className="btn btn-red btn-sm" onClick={() => setPanicModal('all')}>
            ⚠ Panic All
          </button>
        </div>
      </div>

      {/* ═══ TODAY'S STRATEGIES ═══ */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">📊 Today's Strategies <InfoTip term="executionSet" /></span>
          <span className="kpi-sub" style={{ marginLeft: 'auto' }}>{today.length} set{today.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="exec-card-list">
          {today.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
              <p style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>No strategies running today</p>
              <p style={{ fontSize: '0.8125rem' }}>Click <strong>+ New Set</strong> to create an execution set</p>
            </div>
          ) : (
            today.map((exec) => renderExecCard(exec))
          )}
        </div>
      </div>

      {/* ═══ ACTIVE STRATEGIES (overnight/multi-day — future use) ═══ */}
      <div className="card" style={{ marginTop: '1rem' }}>
        <div className="card-header">
          <span className="card-title">🌙 Active Strategies (Overnight)</span>
          <span className="kpi-sub" style={{ marginLeft: 'auto' }}>{active.length} set{active.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="exec-card-list">
          {active.length === 0 ? (
            <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.8125rem' }}>
              No overnight strategies active — this section is for future multi-day strategies
            </div>
          ) : (
            active.map((exec) => renderExecCard(exec))
          )}
        </div>
      </div>

      {/* ═══ ARCHIVED LINK ═══ */}
      <div style={{ marginTop: '1rem', textAlign: 'center' }}>
        <button
          className="btn btn-ghost"
          onClick={() => _onNavigate('trader-archived')}
          style={{ fontSize: '0.875rem', gap: '0.5rem' }}
        >
          📁 View Archived Strategies ({archived.length} sets, last 90 days) →
        </button>
      </div>


      {/* Partial Exit Modal */}
      {partialModal && (() => {
        const exec = execs.find((e) => e.id === partialModal)
        const p = exec?.params as any
        const totalLots = p?.quantityLots || 1
        const lotSize = getLotSize(exec?.underlying || 'NIFTY')
        const totalQty = totalLots * lotSize

        // Generate valid percentage options based on lot count
        // Each option must result in a whole number of lots
        const generateOptions = (lots: number): { pct: number; lotCount: number }[] => {
          const opts: { pct: number; lotCount: number }[] = []
          // Always include 0
          opts.push({ pct: 0, lotCount: 0 })
          if (lots <= 10) {
            // For small lot counts, show every possible lot
            for (let i = 1; i < lots; i++) {
              opts.push({ pct: Math.round((i / lots) * 100), lotCount: i })
            }
          } else {
            // For larger lot counts, show at reasonable intervals
            const steps = new Set<number>()
            for (let pct = 10; pct < 100; pct += 10) {
              const lotCount = Math.floor(lots * pct / 100)
              if (lotCount > 0 && lotCount < lots && !steps.has(lotCount)) {
                steps.add(lotCount)
                opts.push({ pct: Math.round((lotCount / lots) * 100), lotCount })
              }
            }
          }
          // Always include 100%
          opts.push({ pct: 100, lotCount: lots })
          return opts
        }

        const ceOptions = generateOptions(totalLots)
        const peOptions = generateOptions(totalLots)

        // Calculate lots from selected percentage
        const ceLots = Math.floor(totalLots * partialCe / 100)
        const peLots = Math.floor(totalLots * partialPe / 100)
        const ceQty = ceLots * lotSize
        const peQty = peLots * lotSize

        return (
          <div className="modal-overlay" onClick={() => setPartialModal(null)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Partial Exit — {exec?.setName || partialModal}</h3>
                <button className="modal-close" onClick={() => setPartialModal(null)}>✕</button>
              </div>
              <div className="modal-body">
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.8125rem', marginBottom: '0.5rem' }}>
                  Select number of lots to exit for each leg.
                </p>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
                  Total: {totalLots} Lot{totalLots > 1 ? 's' : ''} × {lotSize} = {totalQty} Qty per leg
                </div>
                <div className="cfg-grid">
                  <div className="cfg-field">
                    <label className="cfg-label">CE Leg — {ceLots} Lot{ceLots !== 1 ? 's' : ''} ({ceQty} Qty)</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
                      {ceOptions.map((opt) => (
                        <button key={opt.pct} className={`btn btn-sm ${partialCe === opt.pct ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setPartialCe(opt.pct)} style={{ minWidth: 44 }}>
                          {opt.lotCount}L
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="cfg-field">
                    <label className="cfg-label">PE Leg — {peLots} Lot{peLots !== 1 ? 's' : ''} ({peQty} Qty)</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
                      {peOptions.map((opt) => (
                        <button key={opt.pct} className={`btn btn-sm ${partialPe === opt.pct ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setPartialPe(opt.pct)} style={{ minWidth: 44 }}>
                          {opt.lotCount}L
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                {exec && (
                  <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'var(--bg-input)', borderRadius: 'var(--radius-sm)', fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                    Exit: CE {ceLots} lot{ceLots !== 1 ? 's' : ''} ({ceQty} qty) + PE {peLots} lot{peLots !== 1 ? 's' : ''} ({peQty} qty)
                    {(ceLots > 0 || peLots > 0) && (
                      <> — Est. P&L: <strong style={{ color: exec.state.pnl >= 0 ? 'var(--green)' : 'var(--red)' }}>
                        ₹{Math.round(exec.state.pnl * ((partialCe + partialPe) / 200)).toLocaleString()}
                      </strong></>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-ghost" onClick={() => setPartialModal(null)}>Cancel</button>
                <button className="btn btn-primary" onClick={handlePartialExit} disabled={ceLots === 0 && peLots === 0}>
                  Exit {ceLots + peLots} Lot{(ceLots + peLots) !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        )
      })()}

      {/* Panic Exit Modal */}
      {panicModal && (
        <div className="modal-overlay" onClick={() => setPanicModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ color: 'var(--red)' }}>⚠ Panic Exit</h3>
              <button className="modal-close" onClick={() => setPanicModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.8125rem', marginBottom: '1rem' }}>
                Square off <strong>{panicModal === 'all' ? 'ALL POSITIONS' : panicModal}</strong> immediately?
              </p>
              <div style={{ background: 'var(--red-glow)', padding: '0.75rem', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(239,68,68,0.2)', fontSize: '0.8125rem' }}>
                This action will immediately square off all open positions. This cannot be undone.
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setPanicModal(null)}>Cancel</button>
              <button className="btn btn-red" onClick={handlePanicExit}>
                ⚠ Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Shared Sidebar for creating new sets ═══ */}
      {showSidebar && (
        <TraderSetSidebar
          onClose={() => setShowSidebar(false)}
          onCreate={handleCreateFromSidebar}
        />
      )}
    </div>
  )
}
